import React, { useEffect, useState } from "react";
import { Send, Save, ArrowLeft, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, statusTone, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CourseForm, EMPTY_COURSE } from "@/components/course/CourseForm";
import { Curriculum } from "@/components/course/Curriculum";
import { CourseExtras } from "@/components/course/CourseExtras";
import { toast } from "sonner";

const toForm = (c) => Object.fromEntries(Object.keys(EMPTY_COURSE).map((k) => [k, c[k] ?? EMPTY_COURSE[k]]));
const clean = (f) => ({
  ...f,
  what_you_learn: f.what_you_learn.map((x) => x.trim()).filter(Boolean),
  highlights: f.highlights.map((x) => x.trim()).filter(Boolean),
  requirements: f.requirements.map((x) => x.trim()).filter(Boolean),
  faqs: f.faqs.filter((x) => x.q?.trim() && x.a?.trim()),
});

function StatusActions({ c, isAdmin, setStatus }) {
  if (isAdmin) {
    return c.status === "published"
      ? <Button variant="outline" onClick={() => setStatus("unpublished")} data-testid="admin-unpublish">Unpublish</Button>
      : <Button className="viriks-amber text-white font-semibold" onClick={() => setStatus("published")} data-testid="admin-publish"><Send size={15} className="mr-1" /> Publish</Button>;
  }
  if (["draft", "rejected", "unpublished"].includes(c.status)) {
    return <Button className="viriks-amber text-white font-semibold" onClick={() => setStatus("submitted")} data-testid="submit-review"><Send size={15} className="mr-1" /> Submit for Review</Button>;
  }
  return null;
}

export default function CourseEditor({ courseId, tab = "details", cats, onBack }) {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin", "sub_admin"].includes(user?.role);
  const [c, setC] = useState(null);
  const [f, setF] = useState(EMPTY_COURSE);
  const [saving, setSaving] = useState(false);

  const reload = () => api.get(`/courses/${courseId}`).then(({ data }) => { setC(data); return data; });
  useEffect(() => { reload().then((d) => setF(toForm(d))); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Course title is required");
    setSaving(true);
    try { const body = clean(f); await api.put(`/courses/${courseId}`, body); setF(body); toast.success("Course saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const setStatus = async (status) => {
    try { await api.post(`/courses/${courseId}/status`, { status, remark: status === "submitted" ? "Submitted by educator" : "" }); toast.success(`Course ${status}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!c) return <Loading />;
  return (
    <div data-testid="course-editor">
      <button onClick={onBack} className="text-sm text-muted-foreground mb-4 inline-flex items-center gap-1 hover:text-navy" data-testid="back-to-courses"><ArrowLeft size={14} /> Back to courses</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-navy truncate" data-testid="editor-title">{c.title}</h1>
          <div className="mt-1 flex items-center gap-2"><Badge tone={statusTone(c.status)}>{c.status}</Badge><span className="text-xs text-muted-foreground">{c.sections?.length || 0} sections • {c.enrolled_count} students</span></div>
        </div>
        <div className="flex gap-2">
          <a href={`/courses/${c.id}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="editor-preview"><Eye size={15} className="mr-1" /> Preview</Button></a>
          <StatusActions c={c} isAdmin={isAdmin} setStatus={setStatus} />
        </div>
      </div>
      {c.review_remark && c.status === "rejected" && <div className="bg-crimson/10 border border-crimson/20 rounded-lg p-4 mb-6 text-sm text-crimson">Admin remark: {c.review_remark}</div>}

      <Tabs defaultValue={tab}>
        <TabsList className="mb-4">
          <TabsTrigger value="details" data-testid="tab-details">Course Details</TabsTrigger>
          <TabsTrigger value="extras" data-testid="tab-extras">Highlights & FAQs</TabsTrigger>
          <TabsTrigger value="curriculum" data-testid="tab-curriculum">Curriculum</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div className="bg-white border border-border rounded-lg p-6 max-w-3xl">
            <CourseForm f={f} set={set} cats={cats} />
            <div className="mt-6 flex justify-end"><Button className="viriks-navy text-white" disabled={saving} onClick={save} data-testid="save-course"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save Changes"}</Button></div>
          </div>
        </TabsContent>
        <TabsContent value="extras">
          <CourseExtras f={f} set={set} />
          <div className="mt-4 flex justify-end"><Button className="viriks-navy text-white" disabled={saving} onClick={save} data-testid="save-extras"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save Changes"}</Button></div>
        </TabsContent>
        <TabsContent value="curriculum">
          <Curriculum courseId={c.id} sections={c.sections || []} reload={reload} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
